import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useStaticQuery, graphql } from 'gatsby';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { getMenuLinkClasses } from '../utils/utils';
import { MenuFields } from '../graphql-fragments/MenuFieldsFragment';
import { LocationContext } from '../contexts/LocationContext';
import { HIDDEN_FEATURES } from '../constants';

import * as styles from './DropDownMenu.module.scss';

type DropDownMenuFields = MenuFields & {
  allSanityResourceVideo: { totalCount: number };
  allSanityResourceLink: { totalCount: number };
};

const DropDownMenu = (): React.ReactElement => {
  const { t } = useTranslation();
  const location = useContext(LocationContext);
  const data: DropDownMenuFields = useStaticQuery(
    graphql`
      {
        ...MenuFields
      }
    `,
  );

  const {
    sanityWhoWeAre: whoWeAre,
    sanityResourcesPage: resourcesPage,
    allSanityResourceVideo: allResourceVideos,
    allSanityResourceLink: allResourceLinks,
  } = data;

  const entries = [
    {
      title: t('menu.who_we_are', 'Who we are'),
      subEntries: [
        {
          title: t('ascenza_page.title', 'ASCENZA'),
          url: '/who-we-are/ascenza',
        },
        {
          title: t('team_and_contacts_page.title', 'Team and Contacts'),
          url: '/who-we-are/team-and-contacts',
        },
        {
          title: t('rovensa_page.title', 'ROVENSA'),
          url: '/who-we-are/rovensa',
        },
        ...whoWeAre.extraSections.map(extraSection => ({
          title: extraSection.title,
          url: '/who-we-are/' + extraSection.slug.current,
        })),
      ],
    },
    ...(!HIDDEN_FEATURES.includes('products')
      ? [
          {
            title: t('menu.products', 'Products'),
            url: '/products',
          },
        ]
      : []),
    ...(!HIDDEN_FEATURES.includes('distributors_page')
      ? [
          {
            title: t('menu.distributors', 'Distributors'),
            url: '/distributors',
          },
        ]
      : []),
    ...(data.allSanityNewsArticle.totalCount > 0
      ? [
          {
            title: t('menu.news', 'News'),
            url: '/news',
          },
        ]
      : []),
    ...(data?.sanityResourcesPage?.articlesAndFilesSections &&
    data.sanityResourcesPage.articlesAndFilesSections.length +
      data.allSanityResourceVideo.totalCount +
      data.allSanityResourceLink.totalCount >
      0
      ? [
          {
            title: t('menu.resources', 'Resources'),
            subEntries: [
              ...resourcesPage.articlesAndFilesSections.map(subEntry => {
                if (subEntry.__typename === 'SanitySubMenuSections') {
                  return {
                    title: subEntry.title,
                    entries: subEntry.articlesAndFilesSections.map(thirdLevelEntry => ({
                      title: thirdLevelEntry.title,
                      url: '/resources/' + thirdLevelEntry.slug.current,
                    })),
                  };
                } else {
                  return {
                    title: subEntry.title,
                    url: '/resources/' + subEntry.slug.current,
                  };
                }
              }),
              ...(allResourceVideos.totalCount > 0
                ? [
                    {
                      title: t('videos_page.title', 'Videos'),
                      url: '/resources/videos',
                    },
                  ]
                : []),
              ...(allResourceLinks.totalCount > 0
                ? [
                    {
                      title: t('useful_links_page.title', 'Useful Links'),
                      url: '/resources/useful-links',
                    },
                  ]
                : []),
            ],
          },
        ]
      : []),
    {
      title: t('menu.contact_us', 'Contact us'),
      url: '/contactUs',
    },
  ];

  return (
    <div className={styles.dropMenuContainer}>
      {entries.map((entry, i) => (
        <div key={i} className={styles.sectionLinkContainer}>
          {entry.url !== undefined ? (
            <Link
              to={entry.url}
              className={
                styles.sectionLink +
                (location
                  ? getMenuLinkClasses(
                      location.pathname,
                      entry.url,
                      entry.url,
                      styles.selected,
                      styles.disabled,
                    )
                  : '')
              }
            >
              {entry.title}
            </Link>
          ) : (
            <>
              <span className={styles.sectionLinkTitle}>{entry.title}</span>
              {entry.subEntries.map((subEntry, i) =>
                subEntry.entries ? (
                  <div key={i} className={styles.subEntryContainer}>
                    <span className={styles.subEntryTitle}>{subEntry.title}</span>
                    <div className={styles.thirdLevelLinksContainer}>
                      {subEntry.entries.map((thirdLevelEntry, i: number) => (
                        <Link
                          key={i}
                          to={thirdLevelEntry.url}
                          className={
                            styles.thirdLevelLink +
                            (location
                              ? getMenuLinkClasses(
                                  location.pathname,
                                  thirdLevelEntry.url,
                                  thirdLevelEntry.url,
                                  styles.selected,
                                  styles.disabled,
                                )
                              : '')
                          }
                        >
                          {thirdLevelEntry.title}
                        </Link>
                      ))}
                    </div>
                    <div className={styles.thirdLevelEntries}></div>
                  </div>
                ) : (
                  <Link
                    key={i}
                    to={subEntry.url}
                    className={
                      styles.innerSectionLink +
                      (location
                        ? getMenuLinkClasses(
                            location.pathname,
                            subEntry.url,
                            subEntry.url,
                            styles.selected,
                            styles.disabled,
                          )
                        : '')
                    }
                  >
                    {subEntry.title}
                  </Link>
                ),
              )}
            </>
          )}
        </div>
      ))}
    </div>
  );
};

export default DropDownMenu;
